import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_BASE, SOCKET_BASE, AUTH_TOKEN } from '../config';

// useMeetSession — join a live two-person session from INSIDE the world.
//
// A trimmed port of the website studio's useCollabSession: same `/collab`
// socket namespace, same REST pairing (codes), same deterministic WebRTC
// (whoever is already in the room initiates), same teardown discipline. What
// is deliberately NOT here: session creation UI, the Reactor X2 avatar-camera
// swap, and the arrangement (`studio_state`) sync — the world side of a meet
// is the VISITOR standing in the room; the artist runs the full web collab
// tab (with X2, background plate and all) and their stream lands on the
// room's video panel. Incoming `studio_state` is ignored, not an error — the
// web host broadcasts it to any peer and a meet has no timeline to apply it to.
//
// The peer's stream is handed back raw; MeetDock owns the DOM <video> and the
// liveVideoEl handoff to the 3D panel.

const FALLBACK_ICE = [{ urls: ['stun:stun.l.google.com:19302'] }];

export default function useMeetSession() {
  // idle | starting | waiting | connecting | live | ended | error
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [peer, setPeer] = useState(null); // { socketId, name, handle, appearingAs }
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mediaNote, setMediaNote] = useState('');

  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const mediaGenRef = useRef(0);        // bumped on teardown; stale getUserMedia self-stops
  const mediaPromiseRef = useRef(null); // in-flight getUserMedia (single-flight)
  const sessionGenRef = useRef(0);      // bumped on teardown; cancels in-flight joins
  const pendingIceRef = useRef([]);
  const iceServersRef = useRef(FALLBACK_ICE);

  const closePeer = useCallback(() => {
    if (pcRef.current) {
      try { pcRef.current.close(); } catch { /* already closed */ }
      pcRef.current = null;
    }
    pendingIceRef.current = [];
    setRemoteStream(null);
    setPeer(null);
  }, []);

  const stopMedia = useCallback(() => {
    mediaGenRef.current += 1;
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
  }, []);

  const teardown = useCallback((nextPhase = 'idle') => {
    sessionGenRef.current += 1;
    closePeer();
    stopMedia();
    if (socketRef.current) {
      try { socketRef.current.disconnect(); } catch { /* already gone */ }
      socketRef.current = null;
    }
    setPhase(nextPhase);
  }, [closePeer, stopMedia]);

  useEffect(() => () => teardown('idle'), [teardown]);

  const sendSignal = useCallback((to, data) => {
    socketRef.current?.emit('rtc_signal', { to, data });
  }, []);

  const createPeerConnection = useCallback((peerSocketId, initiator) => {
    closePeer();
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pcRef.current = pc;

    const outgoing = localStreamRef.current;
    if (outgoing) outgoing.getTracks().forEach((track) => pc.addTrack(track, outgoing));

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(peerSocketId, { type: 'ice', candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (stream) setRemoteStream(stream);
    };
    pc.onconnectionstatechange = () => {
      if (pc !== pcRef.current) return;
      if (pc.connectionState === 'connected') setPhase('live');
      if (pc.connectionState === 'disconnected') setPhase((p) => (p === 'live' ? 'connecting' : p));
    };

    if (initiator) {
      (async () => {
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendSignal(peerSocketId, { type: 'offer', sdp: pc.localDescription });
        } catch (err) {
          console.error('[meet] offer failed', err);
        }
      })();
    }
    return pc;
  }, [closePeer, sendSignal]);

  const handleSignal = useCallback(async ({ from, data }) => {
    try {
      if (data.type === 'offer') {
        const pc = pcRef.current || createPeerConnection(from, false);
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        for (const c of pendingIceRef.current) {
          try { await pc.addIceCandidate(c); } catch { /* stale candidate */ }
        }
        pendingIceRef.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendSignal(from, { type: 'answer', sdp: pc.localDescription });
      } else if (data.type === 'answer') {
        if (!pcRef.current) return;
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
        for (const c of pendingIceRef.current) {
          try { await pcRef.current.addIceCandidate(c); } catch { /* stale candidate */ }
        }
        pendingIceRef.current = [];
      } else if (data.type === 'ice' && data.candidate) {
        if (pcRef.current?.remoteDescription) {
          await pcRef.current.addIceCandidate(data.candidate);
        } else {
          pendingIceRef.current.push(data.candidate);
        }
      }
    } catch (err) {
      console.error('[meet] signal handling failed', err);
    }
  }, [createPeerConnection, sendSignal]);

  const acquireMedia = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    if (mediaPromiseRef.current) return mediaPromiseRef.current;
    const gen = mediaGenRef.current;
    mediaPromiseRef.current = (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: true,
        });
        if (mediaGenRef.current !== gen) {
          // Torn down while the permission prompt was up — no orphaned camera.
          stream.getTracks().forEach((t) => t.stop());
          return null;
        }
        localStreamRef.current = stream;
        setLocalStream(stream);
        setMicOn(true);
        setCamOn(true);
        setMediaNote('');
        return stream;
      } catch (err) {
        // A meet without your camera still works — you see and hear the artist.
        setMediaNote('Camera/mic unavailable — joining view-only.');
        return null;
      } finally {
        mediaPromiseRef.current = null;
      }
    })();
    return mediaPromiseRef.current;
  }, []);

  const toggleMic = useCallback(() => {
    const tracks = localStreamRef.current?.getAudioTracks() || [];
    const next = !tracks.some((t) => t.enabled);
    tracks.forEach((t) => { t.enabled = next; });
    setMicOn(next);
  }, []);

  const toggleCam = useCallback(() => {
    const tracks = localStreamRef.current?.getVideoTracks() || [];
    const next = !tracks.some((t) => t.enabled);
    tracks.forEach((t) => { t.enabled = next; });
    setCamOn(next);
  }, []);

  const connectAndJoin = useCallback((joinCode) => {
    const socket = io(`${SOCKET_BASE}/collab`, {
      auth: { token: AUTH_TOKEN },
      path: '/socket.io',
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // First connect and every reconnect. On reconnect our socket id changed,
      // so drop the old peer connection — the far side re-offers on our join.
      closePeer();
      socket.emit('join_session', { code: joinCode, appearingAs: null });
    });

    socket.on('session_joined', ({ peers }) => {
      if (peers.length > 0) {
        setPeer(peers[0]);
        setPhase('connecting'); // they're here — they initiate, we answer
      } else {
        setPhase('waiting');
      }
    });

    socket.on('peer_joined', (p) => {
      setPeer(p);
      setPhase('connecting');
      createPeerConnection(p.socketId, true);
    });

    socket.on('rtc_signal', handleSignal);
    socket.on('peer_appearance', ({ socketId, appearingAs }) => {
      setPeer((p) => (p && p.socketId === socketId ? { ...p, appearingAs } : p));
    });
    socket.on('studio_state', () => { /* a meet has no timeline — ignored */ });

    socket.on('peer_left', () => {
      closePeer();
      setPhase('waiting');
    });
    socket.on('session_ended', () => teardown('ended'));
    socket.on('collab_error', ({ message }) => {
      setError(message || 'Something went wrong.');
      teardown('error');
    });
    socket.on('connect_error', (err) => {
      setError(err?.message || 'Could not connect.');
      teardown('error');
    });
  }, [closePeer, createPeerConnection, handleSignal, teardown]);

  /** Join an existing session by code (the meet link / notification carries it). */
  const join = useCallback(async (joinCode) => {
    const normalized = String(joinCode || '').trim().toUpperCase();
    if (!normalized) return;
    if (phase !== 'idle' && phase !== 'ended' && phase !== 'error') return;
    setError('');
    setPhase('starting');
    const gen = sessionGenRef.current;
    try {
      const [res] = await Promise.all([
        fetch(`${API_BASE}/api/collab/session/${normalized}`, {
          headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
        }),
        // ICE config + camera in parallel with the lookup.
        fetch(`${API_BASE}/api/collab/config`)
          .then((r) => r.json())
          .then((d) => { if (Array.isArray(d.iceServers) && d.iceServers.length) iceServersRef.current = d.iceServers; })
          .catch(() => { /* fallback STUN already set */ }),
        acquireMedia(),
      ]);
      if (gen !== sessionGenRef.current) return; // cancelled while setting up
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Session not found or expired.');
      if (d.full) throw new Error('This session already has two people in it.');
      connectAndJoin(normalized);
    } catch (err) {
      if (gen !== sessionGenRef.current) return;
      setError(err.message);
      teardown('error');
    }
  }, [phase, acquireMedia, connectAndJoin, teardown]);

  /** Leave the meet (the artist's session survives; the code stays valid). */
  const leave = useCallback(() => {
    socketRef.current?.emit('leave_session');
    teardown('idle');
  }, [teardown]);

  return {
    phase,
    live: phase === 'live',
    active: phase !== 'idle' && phase !== 'ended' && phase !== 'error',
    error,
    mediaNote,
    peer,
    localStream,
    remoteStream,
    micOn,
    camOn,
    join,
    leave,
    toggleMic,
    toggleCam,
  };
}
